import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Pencil, Search, X } from "lucide-react";

import { Product } from "../../models/setup/product";
import { LabelValuePair } from "../../models/label-value";

import TableContainer from "../../common/table-container";
import LoadingComponent from "../../common/components/loader";

import { BrandingData } from "../../admin-portal/settings/branding";

import { GetProducts, GetTaxes } from "../../api/setup";

interface Props {
  viewOnly?: boolean;
  onEditProduct?: (customer: Product) => void;
}

const ProductListComponent: React.FC<Props> = ({ viewOnly, onEditProduct }) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);

  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  //const [taxes, setTaxes] = useState<LabelValuePair[]>([]);

  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    //loadTaxes();

    loadData();
  }, []);

  /*  const loadTaxes = async () => {
    const res = await GetTaxes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setTaxes(_st);
    } else {
      setTaxes([]);
    }
  }; */
  const loadData = async (param?: any) => {
    //param?: TenantSearchParam
    setLoading(true);
    const _param: any = param ?? {};

    const res = await GetProducts(_param);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setProducts(res?.data ?? []);
      setTotal(res?.data?.length ?? 0);
    } else {
      setProducts([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: `Price`,
        accessorKey: "price",
        headerStyle: { textAlign: "right" },
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the fee to 2 decimal places with commas
          const formattedTotal = cell.row.original.price
            ? new Intl.NumberFormat().format(cell.row.original.price.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists
          return (
            <>
              <p className="text-right"> {formattedTotal}</p>
            </>
          );
        },
      },
      {
        header: "Cost",
        accessorKey: "cost",
        headerStyle: { textAlign: "right" },
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the fee to 2 decimal places with commas
          const formattedTotal = cell.row.original.cost
            ? new Intl.NumberFormat().format(cell.row.original.cost.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists
          return (
            <>
              <p className="text-right"> {formattedTotal}</p>
            </>
          );
        },
      },
      {
        header: "Discount",
        accessorKey: "discount",
        headerStyle: { textAlign: "right" },
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Format the fee to 2 decimal places with commas
          let _total = cell.row.original.price ?? 0.0;
          let _discount = 0.0;
          if ((cell.row.original.discountPercentage ?? "") != "") {
            _discount = parseFloat(cell.row.original.discountPercentage);
            _total = _total - _total * 0.01 * _discount;
          } else if ((cell.row.original.discountAmount ?? "") != "") {
            _discount = parseFloat(cell.row.original.discountAmount);
            _total = _total - _discount;
          }
          return (
            <>
              {_discount > 0 && (
                <div className="text-right pr-10">
                  <p>
                    Discount: {_discount} {(cell.row.original.discountPercentage ?? "") != "" ? "%" : ""}{" "}
                  </p>
                  <p> Discounted Price:{Intl.NumberFormat().format(_total.toFixed(2))}</p>
                </div>
              )}
            </>
          );
        },
      },

      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { isActive, id } = cell.row.original;

          return (
            <div className="flex items-center">
              <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                <input
                  type="checkbox"
                  id={`status-${id}`} // unique ID for each row
                  checked={isActive}
                  readOnly // making checkbox read-only to avoid user interaction
                  className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                />
                <label
                  htmlFor={`status-${id}`}
                  className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer transition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                ></label>
              </div>
            </div>
          );
        },
      },
    ];
    if (!(viewOnly ?? false)) {
      baseColumns.push({
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => {
                if (onEditProduct) onEditProduct(cell.row.original);
              }}
            >
              <Pencil className="size-4" />
            </div>
          </div>
        ),
      });
    }
    return baseColumns;
  }, []);

  return (
    <React.Fragment>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5 min-w-[50%]">
        {/*  <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">{saving && <LoadingComponent />}</div>
          </div>
        </div> */}
        <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">
              <div className="grid grid-cols-1 gap-4 mb-5 xl:grid-cols-12">
                <div className="xl:col-span-10">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search by Name"
                      autoComplete="off"
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>
                <div className="xl:col-span-2">
                  <div className="ltr:lg:text-right rtl:lg:text-left">
                    <Link
                      to=""
                      onClick={() => {
                        const _param = {
                          searchText: searchText,
                        };
                        loadData(_param);
                      }}
                      type="button"
                      className="text-white btn  min-w-40 bg-custom-800 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    >
                      <span className="align-middle">Search</span>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 mb-5 xl:grid-cols-12">
                <div className="xl:col-span-12">
                  <div className="mx-auto w-full">
                    {loading ? (
                      <LoadingComponent></LoadingComponent>
                    ) : (
                      <>
                        <div className="overflow-x-auto mt-2">
                          {products && products.length > 0 ? (
                            <TableContainer
                              isPagination={true}
                              columns={columns || []}
                              data={products || []}
                              totalCount={total}
                              customPageSize={pageSize}
                              divclassName="overflow-x-auto"
                              tableclassName="w-full whitespace-nowrap"
                              theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                              thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                              tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                            />
                          ) : (
                            <div className="noresult">
                              <div className="py-6 text-center">
                                {/*   <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" /> */}
                                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                                <p className="mb-0 text-slate-500 dark:text-zink-200">
                                  No products setup for this {BrandingData.bussinessDomain.toLocaleLowerCase()}, please
                                  create new product.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default ProductListComponent;
